import "./assets.css";
import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth } from "../../../hooks/AuthProvider";
import API_BASE_URL from "../../../config/api";
import { Modal, Button } from "react-bootstrap";

const MAX_ASSET_FILES = 60;

const TABS = [
  { key: "sticker", label: "Stickers", hasCategory: true, hasItems: true },
  { key: "filter", label: "Filters", hasCategory: true, hasItems: true },
  { key: "memoji", label: "Memoji", hasCategory: true, hasItems: true },
  { key: "wallpaper", label: "Wallpapers", hasCategory: true, hasItems: false },
];

async function uploadToStorage(file, bucket, token, path) {
  const reader = new FileReader();
  const base64 = await new Promise((resolve, reject) => {
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
  const res = await axios.post(
    `${API_BASE_URL}/api/upload`,
    { file: base64, name: path, type: bucket },
    { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
  );
  return res.data.url;
}

function FileDropzone({ multiple, accept, files, onChange, label, maxFiles }) {
  const inputRef = useRef();
  const [dragOver, setDragOver] = useState(false);
  const [previews, setPreviews] = useState([]);

  useEffect(() => {
    if (!files || files.length === 0) { setPreviews([]); return; }
    const urls = [];
    for (const f of files) {
      urls.push(URL.createObjectURL(f));
    }
    setPreviews(urls);
    return () => urls.forEach(URL.revokeObjectURL);
  }, [files]);

  const addFiles = (incoming) => {
    if (!incoming?.length) return;
    const accepts = accept ? accept.split(',').map(t => t.trim()).filter(Boolean) : [];
    const filtered = accepts.length
      ? [...incoming].filter(f => accepts.some(t => {
        if (t === '*/*') return true;
        if (t.endsWith('/*')) return f.type.startsWith(t.slice(0, -1));
        if (t.startsWith('.')) return f.name.toLowerCase().endsWith(t.toLowerCase());
        return f.type === t;
      }))
      : [...incoming];
    if (!filtered.length) return;
    const current = files || [];
    if (maxFiles && current.length + filtered.length > maxFiles) {
      alert(`Maximum of ${maxFiles} files allowed. You can add ${maxFiles - current.length} more.`);
      return;
    }
    onChange([...current, ...filtered]);
  };

  const removeFile = (index) => {
    const current = [...(files || [])];
    current.splice(index, 1);
    onChange(current.length ? current : null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer.files);
  };

  const remaining = maxFiles ? maxFiles - (files?.length || 0) : Infinity;
  const labelText = maxFiles ? `${label} (max ${maxFiles}, ${remaining} left)` : label;

  return (
    <div className="assetsFormGroup full">
      <label>{labelText} {files?.length ? `— ${files.length} selected` : ''}</label>
      <div
        className={`dropzone ${dragOver ? 'dropzone-active' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
      >
        {previews.length > 0 ? (
          <div className="dropzone-previews">
            {previews.map((url, i) => (
              <div key={i} className="dropzone-thumb">
                <button type="button" className="dropzone-remove" onClick={(e) => { e.stopPropagation(); removeFile(i); }}>&times;</button>
                <img src={url} alt="" />
                <span>{files[i]?.name}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="dropzone-placeholder">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#aaa" strokeWidth="1.5">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            <p>Drop {multiple ? 'files' : 'a file'} here or click to browse</p>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          multiple={multiple}
          accept={accept || '*/*'}
          hidden
          onChange={(e) => {
            addFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </div>
    </div>
  );
}

function Assets() {
  const [activeTab, setActiveTab] = useState("sticker");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [uploadFileList, setUploadFileList] = useState(null);
  const [uploading, setUploading] = useState(false);
  const { token } = useAuth();

  const nameRef = useRef();
  const descRef = useRef();
  const categoryRef = useRef();
  const amountRef = useRef();

  const currentTab = TABS.find((t) => t.key === activeTab);
  const endpoint = `/api/v1/${activeTab}`;
  const multiFile = currentTab.hasItems;

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE_URL}${endpoint}`);
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err.message);
      setItems([]);
    }
    setLoading(false);
  }, [endpoint]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_BASE_URL}${endpoint}/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      console.error(err.message);
    }
    setDeleteTarget(null);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    const name = nameRef.current.value?.trim();
    if (!name) { alert("Name is required"); return; }
    if (multiFile && (!uploadFileList || uploadFileList.length === 0)) { alert("Please add at least one asset file"); return; }
    setUploading(true);
    try {
      let imageUrl = "";
      let uploadVal = "";
      if (imageFile) imageUrl = await uploadToStorage(imageFile, activeTab, token, `${name}/preview`);
      if (multiFile && uploadFileList) {
        const urls = [];
        for (let i = 0; i < uploadFileList.length; i++) {
          const url = await uploadToStorage(uploadFileList[i], activeTab, token, `${name}/${uploadFileList[i].name}`);
          urls.push(url);
        }
        uploadVal = JSON.stringify(urls);
      } else if (uploadFileList?.length) {
        uploadVal = await uploadToStorage(uploadFileList[0], activeTab, token, name);
      }
      const body = {
        name: name,
        description: descRef.current.value,
        amount: amountRef.current.value,
        image: imageUrl,
        upload: uploadVal,
      };
      if (currentTab.hasCategory) body.category = categoryRef.current.value;
      if (multiFile && activeTab !== "filter") body.items = String(uploadFileList?.length || 0);
      await axios.post(`${API_BASE_URL}${endpoint}`, body, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      setShowForm(false);
      setImageFile(null);
      setUploadFileList(null);
      fetchItems();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to add item");
    }
    setUploading(false);
  };

  const resetForm = () => {
    setShowForm(false);
    setImageFile(null);
    setUploadFileList(null);
  };

  return (
    <div>
      <div className="assetsTabs">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            className={`assetsTab ${activeTab === tab.key ? "active" : ""}`}
            onClick={() => { setActiveTab(tab.key); setShowForm(false); }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="assetsHeader">
        <h3>{currentTab.label}</h3>
        <button className="assetsAddBtn" onClick={() => setShowForm(true)}>
          + Add {currentTab.label.slice(0, -1)}
        </button>
      </div>

      {showForm && (
        <form className="assetsForm" onSubmit={handleAdd}>
          <h4>New {currentTab.label.slice(0, -1)}</h4>
          <div className="assetsFormGrid">
            <div className="assetsFormGroup">
              <label>Name</label>
              <input type="text" ref={nameRef} required />
            </div>
            <div className="assetsFormGroup">
              <label>Amount</label>
              <input type="text" ref={amountRef} placeholder="e.g. $0.99" />
            </div>
            {currentTab.hasCategory && (
              <div className="assetsFormGroup">
                <label>Category</label>
                {activeTab === "sticker" ? (
                  <select ref={categoryRef} defaultValue="animate">
                    <option value="animate">Animate</option>
                    <option value="non-animate">Non-animate</option>
                  </select>
                ) : activeTab === "memoji" ? (
                  <select ref={categoryRef} defaultValue="male">
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                ) : activeTab === "filter" ? (
                  <select ref={categoryRef} defaultValue="effect">
                    <option value="mask">Masks</option>
                    <option value="effect">Effect</option>
                  </select>
                ) : (
                  <input type="text" ref={categoryRef} placeholder="e.g. Premium, Free" />
                )}
              </div>
            )}
            {currentTab.hasItems && activeTab !== "filter" && (
              <div className="assetsFormGroup">
                <label>Items</label>
                <input type="text" value={uploadFileList?.length || 0} disabled className="assetsAutoField" />
              </div>
            )}
            <div className="assetsFormGroup">
              <label>Description</label>
              <input type="text" ref={descRef} placeholder="Short description" />
            </div>
            <FileDropzone
              multiple={false}
              accept="image/*"
              files={imageFile ? [imageFile] : null}
              onChange={(arr) => setImageFile(arr?.[0] || null)}
              label="Preview Image"
            />
            <FileDropzone
              multiple={multiFile}
              accept={multiFile ? (activeTab === "filter" ? undefined : "image/*,application/json") : undefined}
              files={uploadFileList}
              onChange={setUploadFileList}
              label={multiFile ? "Asset Files" : "Asset File"}
              maxFiles={multiFile ? MAX_ASSET_FILES : undefined}
            />
          </div>
          <div className="assetsFormActions">
            <button type="button" className="assetsCancelBtn" onClick={resetForm}>
              Cancel
            </button>
            <button className="assetsSaveBtn" type="submit" disabled={uploading}>
              {uploading ? "Uploading..." : "Save"}
            </button>
          </div>
        </form>
      )}

      {loading && <p className="text-muted">Loading...</p>}

      {!loading && items.length === 0 && !showForm && (
        <div className="assetsEmpty">
          <h4>No {currentTab.label.toLowerCase()} yet</h4>
          <p>Click "Add {currentTab.label.slice(0, -1)}" to create one.</p>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="assetsTable">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                {currentTab.hasCategory && <th>Category</th>}
                <th>Amount</th>
                <th>Date</th>
                <th style={{ width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td style={{ fontWeight: 500 }}>{item.name}</td>
                  {currentTab.hasCategory && <td>{item.category || "-"}</td>}
                  <td>{item.amount || "-"}</td>
                  <td>{new Date(item.created_at).toLocaleDateString()}</td>
                  <td>
                    <button
                      className="assetsDeleteBtn"
                      onClick={() => setDeleteTarget(item.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal show={!!deleteTarget} onHide={() => setDeleteTarget(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this item?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteTarget(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={() => handleDelete(deleteTarget)}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default Assets;
